import useJourney, { Journey } from "common/ui/useJourney";
import { useMemo } from "react";
import { OfferBuilder, OfferFormState } from "./useOfferBuilder";
import useStoreSelector from "common/redux/useStoreSelector";
import { OrderType } from "domain/orderType";
import { CustomField } from "domain/customField";
import useActiveMerchant from "features/merchants/useActiveMerchant";
import Offer from "domain/offer";

export enum ModalJourney {
    Details,
    CustomFields,
    HedgeSettings,
    Confirmation
}

// manual entry has its own hedge toggle inside the step and iceberg needs Max Show, so those two always keep it
// AUT-6484: otherwise the toggle has to be showing too - Auto Offer sets placeHedgeOrder by itself for a
// Determine Later HTA with a hedge setting, and then the hedge comes from that setting
export function shouldShowHedgeSettings(offer: NullishPartial<Offer>, form: OfferFormState): boolean {
    if (offer.orderType === OrderType.ManualEntry || offer.orderType === OrderType.Iceberg) {
        return true;
    }

    return form.placeHedgeOrder === true && form.shouldShowHedgeOrderToggle === true;
}

export default function useOfferModalJourney(
    offerBuilder: OfferBuilder,
    allowedCustomFields: CustomField[],
    onSubmit: () => Promise<void>): Journey<ModalJourney> {
    const activeMerchant = useActiveMerchant();
    const isCustomer = useStoreSelector(m => m.userState.isCustomer);

    const offer = offerBuilder.offer;

    const journeySteps = useMemo(() => {
        const steps = [ModalJourney.Details, ModalJourney.Confirmation];

        if (shouldShowHedgeSettings(offer, offerBuilder.formState)) {
            steps.splice(1, 0, ModalJourney.HedgeSettings);
        }

        if (!isCustomer && activeMerchant.useCustomFields && allowedCustomFields.length > 0) {
            steps.splice(1, 0, ModalJourney.CustomFields);
        }

        return steps;
    }, [offerBuilder.formState.placeHedgeOrder, offerBuilder.formState.shouldShowHedgeOrderToggle, offer.orderType, offer.type, offer.commodity, offer.customerWillSell, allowedCustomFields]);

    return useJourney(journeySteps, onSubmit);
}